import { useEffect } from "react";

/**
 * Lightweight SEO head manager for SPA routes.
 * Updates <title>, meta description/robots, canonical and OG tags.
 * Optionally injects a JSON-LD <script> tag that's removed on unmount.
 */
export default function useSeo({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return el;
    };

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      const el = ensure(`meta[${attr}="${name}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute(attr, name);
        return m;
      });
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", type, "property");
    if (image) setMeta("og:image", image, "property");
    if (canonical) setMeta("og:url", canonical, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (image) setMeta("twitter:image", image);

    let linkCanonical;
    if (canonical) {
      linkCanonical = ensure(`link[rel="canonical"]`, () => {
        const l = document.createElement("link");
        l.rel = "canonical";
        return l;
      });
      linkCanonical.setAttribute("href", canonical);
    }

    // Clear any stale dynamic JSON-LD left over from a prerender snapshot
    // (otherwise we'd hydrate with duplicate blocks).
    document.head
      .querySelectorAll('script[type="application/ld+json"][data-dynamic-seo="true"]')
      .forEach((el) => el.parentNode && el.parentNode.removeChild(el));

    const jsonLdEls = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-dynamic-seo", "true");
        el.textContent = JSON.stringify(block);
        document.head.appendChild(el);
        jsonLdEls.push(el);
      });
    }

    return () => {
      document.title = prevTitle;
      jsonLdEls.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [title, description, canonical, image, type, JSON.stringify(jsonLd || {})]);
}
